import React from "react";
import { Trophy } from "lucide-react";
import { C } from "../constants/design";
import { Player } from "../types/game";
import { COIN_ICON } from "./ui/atoms";
import { BACKGROUND_IMGS, LAYER_IMGS, BOX } from "../constants/items";
import { iSkin } from "../engine/itemEngine";
import { levelProgress } from "../engine/playerEngine";
import { calculateRegeneratedEnergy, formatEnergyTime, MAX_ENERGY } from "../features/league-duel/engine/energy";
import { rankProgress } from "../features/league-duel/engine/duelRanking";
import { computeChestState, claimChestRemote } from "../features/league-duel/engine/dailyChest";
import { sb } from "../lib/supabase";

// ─── Lobby (maquette test) ─────────────────────────────────────────────────────
// Écran d'accueil façon Clash Royale : avatar équipé plein pied, 2 boutons de
// jeu (navigation vers les pages Stadium Trial / Five Duel, ne lance PAS une
// partie), et le coffre quotidien réclamable directement ici.
// Page ajoutée en parallèle des tabs existants — rien n'est supprimé.

const CHARACTER_IMG = "https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/Character.png";
// Même icône que la TopBar globale (const locale dans App.tsx, non exportée).
const TICKET_ICON = "https://i.postimg.cc/d3sX5KSp/ticket-foot-viol-(1).png";

// Largeur de la colonne mobile — alignée sur Shell + BottomNav (jeu mobile,
// on NE déborde PAS sur tout le desktop).
const MOBILE_MAX = 460;
// Polices display/labels reprises de la CampScreen (Stadium Trial) pour l'UI niveau.
const F_DISPLAY = "'Chakra Petch', system-ui, sans-serif";
const F_LABEL = "'Barlow Semi Condensed', system-ui, sans-serif";
// Forme exacte du bouton CampScreen : borderRadius + ce biseau (ST_BEVEL) combinés
// → 2 coins arrondis (haut-droit, bas-gauche) + 2 coins coupés tech (haut-gauche,
// bas-droit). C'est l'intersection des deux clips qui crée ce mix.
const BEVEL = "polygon(0 10px,10px 0,100% 0,100% calc(100% - 10px),calc(100% - 10px) 100%,0 100%)";
// Le fond fixe est centré et plafonné à la colonne mobile (partagé root + calque fond).
const COLUMN_FIXED: React.CSSProperties = {
  position: "fixed", top: 0, bottom: 0, left: 0, right: 0,
  margin: "0 auto", width: "100%", maxWidth: MOBILE_MAX,
};
// Couleurs de rang Duel — miroir de RANK_COLORS dans Classement.tsx (source unique
// = getRankConfig, mais la table couleur n'est pas exportée).
const RANK_COLORS: Record<string, string> = {
  Bronze: "#CD7F32", Silver: "#A8B8C8", Gold: "#F5B301", Platinum: "#C8D0D8",
  Emerald: "#10C068", Ruby: "#E0115F", Diamond: "#7DD3FC",
};

// ── Réglages de la barre du haut (niveau / coins / tickets / feedback) ─────────
// PILL_H = épaisseur des blocs (baisser pour affiner). PILL_BG = fond commun.
// PILL_BORDER = contour commun. Les icônes coins/tickets sont volontairement plus
// grandes que la box → elles débordent symétriquement (haut ET bas), vibe COC.
const PILL_H = 28;
const PILL_BG = "rgba(0,0,0,0.42)";
const PILL_BORDER = "1px solid rgba(255,255,255,0.10)";
// Débordement des icônes hors de la box : agrandies + centrées (pas de translate)
// → elles dépassent en haut ET en bas. Ombre portée pour l'effet "pop".
const PILL_ICON: React.CSSProperties = {
  objectFit: "contain",
  filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.55))",
};

// Ligne joueur telle que servie par App (loadPlayers) — on ne lit ici que le nom
// et la réputation Duel pour classer la position.
interface RankRow {
  player?: { name?: string; duel_reputation?: number | null };
}

export interface LobbyProps {
  me: Player;
  earned: number; // derived.earned — XP de pronos (le niveau = earned + duel_xp)
  gold: number; // wallet affiché (même calcul que la TopBar globale)
  players: RankRow[]; // liste classement (pour la position Duel)
  playersLoaded: boolean;
  onPlayTrial: () => void;
  onPlayDuel: () => void;
  onOpenLeaderboard: () => void;
  onOpenShop: () => void;
  onFeedback: () => void;
  /** Claim DB réussi → App patch me.duel_chest_claimed + ouvre le pack. */
  onChestClaimed: () => void;
}

// ── Gros bouton de jeu — DA CampScreen ────────────────────────────────────────
// Coins biseautés (clipPath), gradient diagonal, profondeur "3D" via ombres
// internes (le clipPath rogne l'ombre externe → on la simule en inset), balayage
// lumineux, titre en police display capitales + sous-titre micro-label traqué.
function GameButton({ onClick, title, subtitle, gradient, edge, accent, shineDelay, notify }: {
  onClick: () => void;
  title: string;
  subtitle: string;
  gradient: string;
  edge: string;    // couleur de l'arête basse (effet relief)
  accent: string;  // couleur du sous-titre + barre d'accent
  shineDelay: string;
  notify?: boolean; // point orange de notification (nouvelle défense)
}) {
  return (
    <button
      onClick={onClick}
      className="lobby-btn"
      style={{
        position: "relative", overflow: "hidden", flex: 1, border: "none",
        padding: "15px 12px", cursor: "pointer", textAlign: "left",
        // Forme CampScreen : rayon + biseau combinés (2 coins ronds + 2 coins coupés).
        background: gradient, borderRadius: 14, clipPath: BEVEL, color: "#fff",
        boxShadow: `inset 0 2px 0 rgba(255,255,255,0.30), inset 0 -6px 0 ${edge}, inset 0 -8px 12px rgba(0,0,0,0.28)`,
      }}
    >
      {notify && (
        <span aria-hidden className="lobby-notify-dot" style={{
          position: "absolute", top: 8, right: 10, zIndex: 3,
          width: 13, height: 13, borderRadius: "50%",
          background: "#FF8A1E", border: "2px solid rgba(0,0,0,0.35)",
        }} />
      )}
      {/* Balayage lumineux (repris de stShine) */}
      <span aria-hidden style={{
        position: "absolute", top: 0, bottom: 0, left: 0, width: "42%",
        background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.22),transparent)",
        transform: "skewX(-22deg)",
        animation: `lobbyShine 4.8s ease-in-out ${shineDelay} infinite`,
        pointerEvents: "none",
      }} />
      <span style={{ position: "relative", zIndex: 1, display: "block" }}>
        <span style={{ display: "block", font: `700 18px ${F_DISPLAY}`, letterSpacing: 1, textTransform: "uppercase", textShadow: "0 1px 6px rgba(0,0,0,0.45)" }}>
          {title}
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
          <span style={{ width: 4, height: 13, borderRadius: 1, background: accent, boxShadow: `0 0 6px ${accent}`, flexShrink: 0 }} />
          <span style={{ font: `800 12px ${F_LABEL}`, letterSpacing: 1.2, textTransform: "uppercase", color: accent, whiteSpace: "nowrap" }}>
            {subtitle}
          </span>
        </span>
      </span>
    </button>
  );
}

// ── Barre du haut partagée (niveau/XP + coins + tickets + feedback) ────────────
// Extraite du Lobby pour être réutilisée en global sur les pages qui avaient la
// TopBar historique (App.tsx). Rend juste la ligne de pills ; le conteneur
// (sticky + padding + voile) est fourni par l'appelant. Le popup XP est local.
export function AppTopBar({
  xp,
  gold,
  tickets,
  onOpenShop,
  onFeedback,
}: {
  xp: ReturnType<typeof levelProgress>;
  gold: number;
  tickets: number;
  onOpenShop?: () => void;
  onFeedback?: () => void;
}) {
  const [xpOpen, setXpOpen] = React.useState(false);
  const xpMaxed = xp.level >= xp.nextLevel;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {/* Pill niveau — badge + barre XP CHUNKY (pleine hauteur), clic → popup détails */}
      <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
        <button
          onClick={() => setXpOpen((o) => !o)}
          style={{
            width: "100%", height: PILL_H, boxSizing: "border-box",
            display: "flex", alignItems: "stretch", gap: 5,
            background: PILL_BG, backdropFilter: "blur(8px)",
            border: xpOpen ? "1px solid rgba(59,240,126,0.5)" : PILL_BORDER,
            borderRadius: 9, padding: 3, cursor: "pointer",
          }}
        >
          {/* Badge niveau — juste le numéro, Chakra Petch, vert DA + reflet */}
          <span style={{
            display: "grid", placeItems: "center", width: PILL_H - 6, flexShrink: 0, borderRadius: 6,
            background: "linear-gradient(150deg,#3BF07E,#159B52)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,.55), 0 1px 4px rgba(0,0,0,.4)",
            font: `700 14px ${F_DISPLAY}`, color: "#04140A", lineHeight: 1,
          }}>
            {xp.level}
          </span>
          {/* Barre XP chunky — remplit toute la hauteur de la pill (façon Clash Royale) */}
          <div style={{ position: "relative", flex: 1, minWidth: 0, borderRadius: 6, background: "rgba(0,0,0,0.5)", border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden", boxShadow: "inset 0 1px 3px rgba(0,0,0,0.55)" }}>
            <div style={{
              position: "absolute", top: 0, bottom: 0, left: 0, width: `${Math.round(xp.ratio * 100)}%`, borderRadius: 6,
              background: "linear-gradient(180deg,#45E8A0,#16A85C)", boxShadow: "0 0 8px rgba(45,227,107,.5)",
              transition: "width .4s cubic-bezier(0.22,1,0.36,1)",
            }}>
              {/* Reflet haut sur toute la longueur du remplissage */}
              <div style={{ position: "absolute", top: 2, left: 3, right: 3, height: 3, borderRadius: 3, background: "linear-gradient(180deg, rgba(255,255,255,0.5), rgba(255,255,255,0))" }} />
            </div>
          </div>
        </button>

        {/* Popup progression (au clic) — détails à la demande, barre compressée */}
        {xpOpen && (
          <>
            <div onClick={() => setXpOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 49 }} />
            <div style={{
              position: "absolute", top: "calc(100% + 7px)", left: 0, zIndex: 50, minWidth: 210,
              background: C.surface, border: `1px solid ${C.line}`, borderRadius: 13,
              padding: "12px 14px", boxShadow: "0 10px 30px rgba(0,0,0,0.55)",
              animation: "lobbyPop .16s ease-out",
            }}>
              {/* Petit bec vers la pill */}
              <div style={{ position: "absolute", top: -6, left: 18, width: 11, height: 11, background: C.surface, borderLeft: `1px solid ${C.line}`, borderTop: `1px solid ${C.line}`, transform: "rotate(45deg)" }} />
              <div style={{ font: `700 16px ${F_DISPLAY}`, color: C.text }}>
                {xp.into.toLocaleString("en-US")} <span style={{ color: C.faint }}>/ {xp.span.toLocaleString("en-US")} XP</span>
              </div>
              <div style={{ position: "relative", height: 8, borderRadius: 4, marginTop: 8, background: "rgba(0,0,0,0.42)", border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: `${Math.round(xp.ratio * 100)}%`, borderRadius: 4, background: "linear-gradient(90deg,#3BF07E,#159B52)" }} />
              </div>
              <div style={{ font: `800 9.5px ${F_LABEL}`, letterSpacing: 1, textTransform: "uppercase", color: C.faint, marginTop: 7 }}>
                {xpMaxed ? "Max level reached" : `${xp.remaining.toLocaleString("en-US")} XP to level ${xp.nextLevel}`}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Pill coins — texte blanc, icône agrandie qui déborde (COC), fond foncé */}
      <button onClick={onOpenShop} style={{
        height: PILL_H, boxSizing: "border-box", display: "flex", alignItems: "center", gap: 5,
        background: PILL_BG, backdropFilter: "blur(8px)",
        border: PILL_BORDER, borderRadius: 9, padding: "0 5px 0 2px",
        fontWeight: 900, fontSize: 13, color: "#fff", cursor: "pointer", flexShrink: 0,
      }}>
        <img src={COIN_ICON} alt="coins" style={{ width: 28, height: 28, ...PILL_ICON }} />
        <span>{gold.toLocaleString("en-US")}</span>
        <span style={{
          display: "inline-grid", placeItems: "center", width: 18, height: 18, borderRadius: 6,
          background: "linear-gradient(180deg,#3BF07E,#159B52)",
          color: "#04140A", fontWeight: 900, fontSize: 14,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,.4)",
        }}>+</span>
      </button>
      {/* Pill tickets — texte blanc, icône agrandie qui déborde (COC), fond foncé */}
      <button onClick={onOpenShop} style={{
        height: PILL_H, boxSizing: "border-box", display: "flex", alignItems: "center", gap: 5,
        background: PILL_BG, backdropFilter: "blur(8px)",
        border: PILL_BORDER, borderRadius: 9, padding: "0 5px 0 2px",
        fontWeight: 900, fontSize: 13, color: "#fff", cursor: "pointer", flexShrink: 0,
      }}>
        <img src={TICKET_ICON} alt="tickets" style={{ width: 30, height: 30, ...PILL_ICON }} />
        <span>{tickets.toLocaleString("en-US")}</span>
        <span style={{
          display: "inline-grid", placeItems: "center", width: 18, height: 18, borderRadius: 6,
          background: "linear-gradient(180deg,#3BF07E,#159B52)",
          color: "#04140A", fontWeight: 900, fontSize: 14,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,.4)",
        }}>+</span>
      </button>
      {/* Bouton feedback — même smiley que la TopBar globale, fond foncé, hauteur PILL_H */}
      <button
        onClick={onFeedback}
        title="Send feedback"
        aria-label="Send feedback"
        style={{
          width: PILL_H, height: PILL_H, boxSizing: "border-box", flexShrink: 0,
          display: "grid", placeItems: "center", padding: 0, // padding:0 = tue le padding UA du <button> → boîte de centrage vraiment 28×28
          background: PILL_BG, backdropFilter: "blur(8px)",
          border: PILL_BORDER, borderRadius: 9,
          color: "rgba(255,255,255,0.7)", cursor: "pointer", // 70% : moins blanc que les icônes coins/tickets
        }}
      >
        {/* display:block supprime l'espace inline. viewBox décalé (minY -1.5) = on
            descend le dessin dans SON repère : la bulle (masse visible, y≈4–17) était
            au-dessus du centre à cause de la queue vide en bas → maintenant centrée. */}
        <svg width="18" height="18" viewBox="0 -1.5 24 24" fill="none" style={{ display: "block" }}>
          <path d="M20 4H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3v3l4-3h9a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
          <circle cx="9" cy="10" r="1.15" fill="currentColor" />
          <circle cx="15" cy="10" r="1.15" fill="currentColor" />
          <path d="M9 12.4c.7 1.4 5.3 1.4 6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" fill="none" />
        </svg>
      </button>
    </div>
  );
}

export function Lobby({ me, earned, gold, players, playersLoaded, onPlayTrial, onPlayDuel, onOpenLeaderboard, onOpenShop, onFeedback, onChestClaimed }: LobbyProps) {
  // Tick 30s : rafraîchit l'énergie régénérée sans re-render global.
  const [, tick] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, []);

  const inv = me.inventory ?? [];
  const eq = me.equipped ?? {};
  const equippedItem = (slot: string) => {
    const iid = eq[slot];
    return iid ? inv.find((x) => x.iid === iid) ?? null : null;
  };
  const layerImg = (slot: string) => {
    const it = equippedItem(slot);
    return it ? (LAYER_IMGS[iSkin(it)] ?? null) : null;
  };
  const bgItem = equippedItem("background");
  const backgroundImg = bgItem ? (BACKGROUND_IMGS[iSkin(bgItem)] ?? null) : null;

  // ── Notif défense : une attaque plus récente que la dernière consultation ? ──
  // Même clé localStorage que l'écran Defenses → le point s'efface à l'ouverture.
  const [hasNewDefenses, setHasNewDefenses] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    const filter = me.profile_id
      ? `defender_profile_id=eq.${me.profile_id}`
      : `defender_name=eq.${encodeURIComponent(me.name)}`;
    sb.get(`duel_history?${filter}&select=played_at&order=played_at.desc&limit=1`)
      .then((rows) => {
        if (cancelled || !rows?.length) return;
        let seen = 0;
        try { seen = Number(localStorage.getItem("duel_defenses_seen_at")) || 0; } catch {}
        setHasNewDefenses(new Date(rows[0].played_at).getTime() > seen);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [me.profile_id, me.name]);

  // L'écran Defenses signale la lecture → on éteint le point orange tout de suite.
  React.useEffect(() => {
    const clear = () => setHasNewDefenses(false);
    window.addEventListener("duel-defenses-seen", clear);
    return () => window.removeEventListener("duel-defenses-seen", clear);
  }, []);

  // ── Niveau / XP ──────────────────────────────────────────────────────────
  // XP passé à AppTopBar (pill niveau + popup progression sont gérés dans le composant).
  const xp = levelProgress(earned + (me.duel_xp ?? 0));

  // ── Rang de réputation Duel : rang + points ELO + position leaderboard ────
  const rep = rankProgress(me.duel_reputation ?? 0);
  const rating = me.duel_reputation ?? null;      // null = jamais joué de duel
  const isRanked = rating != null;
  const rankCol = RANK_COLORS[rep.rank] ?? C.muted;
  // Points restants avant le rang suivant (progressNeeded = span, progressInCurrentRank = acquis).
  const ptsToNext = rep.progressNeeded != null ? Math.max(0, rep.progressNeeded - rep.progressInCurrentRank) : null;

  // Position dans le leaderboard Duel : rang parmi les joueurs ayant une réputation
  // (mêmes données que l'onglet "duel" du Classement, trié par duel_reputation desc).
  const duelPosition = React.useMemo(() => {
    if (!isRanked) return null;
    const dueled = players.filter((p) => p.player?.duel_reputation != null);
    const sorted = [...dueled].sort(
      (a, b) => (b.player!.duel_reputation as number) - (a.player!.duel_reputation as number)
    );
    const idx = sorted.findIndex((p) => p.player?.name === me.name);
    return idx >= 0 ? idx + 1 : null;
  }, [players, me.name, isRanked]);

  // ── Énergie Duel (avec regen) ────────────────────────────────────────────
  const energyState = me.duel_energy_updated_at
    ? calculateRegeneratedEnergy(me.duel_energy ?? 0, me.duel_energy_updated_at)
    : null;

  // ── Coffre quotidien ─────────────────────────────────────────────────────
  const chest = computeChestState(me.duel_chest_date, me.duel_chest_matches, me.duel_chest_claimed);
  const [claiming, setClaiming] = React.useState(false);
  const handleChest = async () => {
    if (!chest.ready || claiming) return;
    setClaiming(true);
    try {
      const res = await claimChestRemote(me.name, chest);
      if (res.claimed) onChestClaimed();
    } finally {
      setClaiming(false);
    }
  };

  return (
    // Calque plein écran (même pattern que le PlayerInspectModal du Classement),
    // MAIS calibré à la colonne mobile (maxWidth 460, centré) — pas de débordement
    // desktop. zIndex:20 < BottomNav (40) : la nav reste visible et cliquable.
    <div style={{ ...COLUMN_FIXED, zIndex: 20, overflowY: "auto", background: C.bg }}>

      {/* ── Fond : démarre TOUT EN HAUT, largeur naturelle (pas d'étirement/crop
           comme le Profil), puis se fond dans le noir vers le bas. ─────────── */}
      <div style={{ ...COLUMN_FIXED, zIndex: 0, overflow: "hidden", pointerEvents: "none", background: C.bg }}>
        {backgroundImg ? (
          <img
            src={backgroundImg}
            alt=""
            style={{
              position: "absolute", top: 0, left: 0, width: "100%", height: "auto", display: "block",
              // Fondu du bas de l'image vers le fond sombre (pas de bord net).
              WebkitMaskImage: "linear-gradient(180deg,#000 0%,#000 58%,transparent 100%)",
              maskImage: "linear-gradient(180deg,#000 0%,#000 58%,transparent 100%)",
            }}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, background: `radial-gradient(120% 78% at 50% 26%, #12203a 0%, #0b1120 52%, ${C.bg} 100%)` }} />
        )}
        {/* Voile léger en haut : lisibilité de la barre niveau/coins/tickets */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 110, background: "linear-gradient(180deg, rgba(6,10,18,0.35) 0%, transparent 100%)" }} />
      </div>

      {/* ── Contenu (scrolle par-dessus le fond ; la colonne est déjà bornée) ── */}
      <div style={{
        position: "relative", zIndex: 1, width: "100%",
        minHeight: "100%", display: "flex", flexDirection: "column", gap: 12,
        padding: "calc(env(safe-area-inset-top, 0px) + 12px) 14px 108px",
      }}>

      {/* ── Barre top (façon Clash Royale) — composant partagé AppTopBar, réutilisé
           en global dans App.tsx sur les pages qui avaient la TopBar historique. ── */}
      <AppTopBar
        xp={xp}
        gold={gold}
        tickets={me.tickets ?? 0}
        onOpenShop={onOpenShop}
        onFeedback={onFeedback}
      />

      {/* ── Rang Duel (carte : rang + points + progression) + bouton leaderboard (position) ── */}
      <div style={{ display: "flex", gap: 10 }}>
        {/* Carte de rang */}
        <button
          onClick={onOpenLeaderboard}
          style={{
            flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 11,
            background: "rgba(18,26,43,0.72)", backdropFilter: "blur(8px)",
            border: `1px solid ${C.line}`, borderRadius: 16,
            padding: "10px 12px", cursor: "pointer", color: C.text, textAlign: "left",
          }}
        >
          {/* Disque de rang teinté */}
          <span style={{
            display: "grid", placeItems: "center", width: 44, height: 44, flexShrink: 0, borderRadius: 13,
            background: `radial-gradient(circle at 50% 34%, ${rankCol}33, rgba(0,0,0,0.28))`,
            border: `1px solid ${rankCol}66`, boxShadow: `inset 0 0 10px ${rankCol}22`,
            fontSize: 23, lineHeight: 1,
          }}>
            {rep.rankIcon}
          </span>

          {/* Rang + points + progression */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 7, minWidth: 0 }}>
              <span style={{ font: `800 15px ${F_DISPLAY}`, letterSpacing: 0.3, color: isRanked ? rankCol : C.muted, whiteSpace: "nowrap" }}>
                {isRanked ? rep.rank : "Unranked"}
              </span>
              {isRanked && (
                <span style={{ font: `700 11px ${F_DISPLAY}`, color: C.muted, whiteSpace: "nowrap" }}>
                  {rating!.toLocaleString("en-US")} pts
                </span>
              )}
            </div>
            {/* Barre de progression vers le rang suivant (vitrée, teinte du rang) */}
            <div style={{ position: "relative", height: 7, borderRadius: 4, marginTop: 5, background: "rgba(0,0,0,0.42)", border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden" }}>
              <div style={{
                position: "absolute", top: 0, bottom: 0, left: 0,
                width: `${Math.round((rep.progressPercent ?? 0) * 100)}%`,
                background: `linear-gradient(90deg,${rankCol},${rankCol}AA)`, borderRadius: 4,
                transition: "width .4s cubic-bezier(0.22,1,0.36,1)",
              }} />
            </div>
            <div style={{ font: `800 9px ${F_LABEL}`, letterSpacing: 1, textTransform: "uppercase", color: C.faint, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {isRanked
                ? (ptsToNext != null ? `${ptsToNext} pts to ${rep.nextRank}` : "Top rank")
                : "Play a duel to get ranked"}
            </div>
          </div>
        </button>

        {/* Bouton leaderboard — porte la position */}
        <button
          onClick={onOpenLeaderboard}
          style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3,
            flexShrink: 0, minWidth: 78,
            background: "rgba(18,26,43,0.72)", backdropFilter: "blur(8px)",
            border: `1px solid ${C.line}`, borderRadius: 16, padding: "8px 12px", cursor: "pointer",
          }}
        >
          <span style={{ font: `800 20px ${F_DISPLAY}`, color: isRanked ? C.text : C.faint, lineHeight: 1 }}>
            {!playersLoaded && isRanked ? "…" : duelPosition != null ? `#${duelPosition}` : "—"}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 4, font: `800 8.5px ${F_LABEL}`, letterSpacing: 0.8, textTransform: "uppercase", color: C.gold }}>
            <Trophy size={11} strokeWidth={2.8} /> Leaderboard
          </span>
        </button>
      </div>

      {/* ── Scène personnage : plus de carte, le perso flotte sur le fond ── */}
      <div style={{
        position: "relative", flex: 1, minHeight: 280,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {/* Halo au sol (ancre le personnage sur le fond) */}
        <div style={{
          position: "absolute", bottom: "10%", left: "50%", transform: "translateX(-50%)",
          width: 210, height: 44, borderRadius: "50%",
          background: `radial-gradient(closest-side, ${C.turf}3D, transparent)`, filter: "blur(3px)",
        }} />

        {/* Personnage + calques — même empilement que Profil (ball < familiar < perso < shoes < top < head).
            Largeur +10% (248 → 273). Ballon et familier ont leur propre flottement
            (durée/phase décalées) → parallaxe légère par rapport au corps. */}
        <div className="lobby-float" style={{ position: "relative", width: 273, zIndex: 1 }}>
          <img src={CHARACTER_IMG} alt="" style={{ display: "block", width: "100%", height: "auto", visibility: "hidden" }} />
          {layerImg("ball") && (
            <img className="lobby-ball" src={layerImg("ball")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 0, pointerEvents: "none" }} />
          )}
          {layerImg("familiar") && (
            <img className="lobby-familiar" src={layerImg("familiar")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 1, pointerEvents: "none" }} />
          )}
          <img src={CHARACTER_IMG} alt="Character" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 2, pointerEvents: "none" }} />
          {layerImg("shoes") && (
            <img src={layerImg("shoes")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 3, pointerEvents: "none" }} />
          )}
          {layerImg("top") && (
            <img src={layerImg("top")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 4, pointerEvents: "none" }} />
          )}
          {layerImg("head") && (
            <img src={layerImg("head")!} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", zIndex: 5, pointerEvents: "none" }} />
          )}
        </div>
      </div>

      {/* ── Boutons de jeu — DA CampScreen (bevel + shine + police display) ── */}
      <div style={{ display: "flex", gap: 11 }}>
        <GameButton
          onClick={onPlayTrial}
          title="Stadium Trial"
          subtitle={`Tier ${me.pve_current_tier ?? 1}`}
          gradient="linear-gradient(135deg,#FF7A2C,#FF2E44)"
          edge="#A81326"
          accent="#FFD2C2"
          shineDelay="0s"
        />
        <GameButton
          onClick={onPlayDuel}
          title="1v1 Duel"
          notify={hasNewDefenses}
          subtitle={
            `⚡ ${energyState ? energyState.energy : "--"}/${MAX_ENERGY}` +
            (energyState && !energyState.isFull ? ` · ${formatEnergyTime(energyState.msToNext)}` : "")
          }
          gradient="linear-gradient(135deg,#4D9AFF,#1D4ED8)"
          edge="#123C99"
          accent="#BFDBFF"
          shineDelay="1.4s"
        />
      </div>

      {/* ── Coffre quotidien ───────────────────────────────────────────── */}
      <button
        onClick={handleChest}
        style={{
          display: "flex", alignItems: "center", gap: 12, textAlign: "left",
          background: "rgba(18,26,43,0.78)", backdropFilter: "blur(8px)",
          borderRadius: 18, padding: "10px 14px",
          border: `1px solid ${chest.ready ? C.gold : C.line}`,
          boxShadow: chest.ready ? `0 0 14px ${C.gold}44` : "none",
          cursor: chest.ready ? "pointer" : "default", color: C.text,
        }}
      >
        <img
          src={BOX.premium}
          alt="Daily chest"
          style={{ width: 46, height: 46, objectFit: "contain", flexShrink: 0, opacity: chest.claimed ? 0.35 : 1 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 900, color: chest.ready ? C.gold : C.text }}>
            {chest.claimed
              ? "Daily Chest claimed — come back tomorrow!"
              : chest.ready
                ? (claiming ? "Opening…" : "Open your free chest!")
                : "Daily Chest"}
          </div>
          {!chest.claimed && !chest.ready && (
            <>
              <div style={{ marginTop: 5, height: 8, borderRadius: 5, background: C.bg, border: `1px solid ${C.lineSoft}`, overflow: "hidden" }}>
                <div style={{
                  width: `${Math.round((chest.matches / chest.goal) * 100)}%`, height: "100%",
                  background: `linear-gradient(90deg,${C.gold},#C98A00)`,
                  transition: "width .4s ease-out",
                }} />
              </div>
              <div style={{ marginTop: 4, fontSize: 10.5, fontWeight: 700, color: C.faint }}>
                Play duels to unlock — {chest.matches} / {chest.goal}
              </div>
            </>
          )}
        </div>
      </button>

      </div>{/* /contenu */}

      {/* Polices CampScreen (Chakra Petch + Barlow Semi Condensed) + flottement
          du personnage (désactivé si prefers-reduced-motion, règle globale). */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Chakra+Petch:wght@600;700;800&family=Barlow+Semi+Condensed:wght@700;800&display=swap');@keyframes lobbyFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}@keyframes lobbyBall{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}@keyframes lobbyFamiliar{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}@keyframes lobbyShine{0%{transform:translateX(-160%) skewX(-22deg)}55%,100%{transform:translateX(360%) skewX(-22deg)}}@keyframes lobbyPop{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}.lobby-float{animation:lobbyFloat 4s ease-in-out infinite}.lobby-ball{animation:lobbyBall 3.4s ease-in-out infinite}.lobby-familiar{animation:lobbyFamiliar 5s ease-in-out -0.8s infinite}.lobby-btn{transition:transform .08s ease}.lobby-btn:active{transform:translateY(2px)}@keyframes notifPulse{0%,100%{transform:scale(1);box-shadow:0 0 0 0 rgba(255,138,30,0.55)}50%{transform:scale(1.18);box-shadow:0 0 8px 3px rgba(255,138,30,0.35)}}.lobby-notify-dot{animation:notifPulse 1.5s ease-in-out infinite}`}</style>
    </div>
  );
}
